"use client";
// Imports
import { Container, ComponentWrapper } from "@/components";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cmsImageUrl } from "@/lib/cmsImageUrl";
import { ArrowLeft, FolderOpen } from "@phosphor-icons/react";
import Image from "next/image";
import { useState } from "react";

// Image
export function DownloadableAssets({ tabs, type, heading }) {
  const [activeTab, setActiveTab] = useState(tabs?.[0]?.name || "");
  const [currentPath, setCurrentPath] = useState({}); // Track current path for each tab

  if (!tabs || tabs.length === 0) {
    return (
      <ComponentWrapper type={type}>
        <Container>
          <h2>Downloadable Assets</h2>
          <p>No assets available for download.</p>
        </Container>
      </ComponentWrapper>
    );
  }

  // Get current folder content for active tab
  const getCurrentFolderContent = (tab) => {
    const path = currentPath[tab.name] || [];
    let current = tab;

    // Navigate to current folder
    for (const folderName of path) {
      current = current.folders?.find((folder) => folder.name === folderName);
      if (!current) return { folders: [], asset: [] };
    }

    return {
      folders: current.folders || [],
      asset: current.asset || [],
    };
  };

  // Navigate to a folder
  const navigateToFolder = (tabName, folderName) => {
    setCurrentPath((prev) => ({
      ...prev,
      [tabName]: [...(prev[tabName] || []), folderName],
    }));
  };

  // Navigate back
  const navigateBack = (tabName) => {
    setCurrentPath((prev) => ({
      ...prev,
      [tabName]: (prev[tabName] || []).slice(0, -1),
    }));
  };

  // Get breadcrumb path
  const getBreadcrumb = (tabName) => {
    const path = currentPath[tabName] || [];
    return path.join(" / ");
  };

  return (
    <ComponentWrapper type={type}>
      <Container>
        {heading && <h2 className="mb-8 text-xl">{heading}</h2>}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="pb-12">
          <TabsList className="w-fit max-w-full overflow-x-auto p-1">
            {tabs.map((tab, index) => (
              <TabsTrigger
                key={index}
                value={tab.name}
                className="group shrink-0 gap-2"
              >
                {tab.name}
                {(tab.folders?.length || 0) + (tab.asset?.length || 0) > 0 && (
                  <span className="inline-flex size-6 items-center justify-center rounded-full bg-foreground/10 text-sm font-semibold tabular-nums text-foreground group-data-[state=active]:bg-white/20 group-data-[state=active]:text-white">
                    {(tab.folders?.length || 0) + (tab.asset?.length || 0)}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map((tab, index) => {
            const { folders, asset: assets } = getCurrentFolderContent(tab);
            const breadcrumb = getBreadcrumb(tab.name);
            const isInSubfolder = (currentPath[tab.name] || []).length > 0;

            return (
              <TabsContent key={index} value={tab.name} className="pt-8">
                <div className="space-y-4">
                  {/* Breadcrumb */}
                  {breadcrumb && (
                    <div className="text-sm text-gray-600">
                      {tab.name} / {breadcrumb}
                    </div>
                  )}

                  {folders.length > 0 || assets.length > 0 || isInSubfolder ? (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                      {/* Back button when in subfolder */}
                      {isInSubfolder && (
                        <button
                          type="button"
                          className="p-2 w-full text-left bg-brand-wool rounded border hover:bg-gray-50"
                          onClick={() => navigateBack(tab.name)}
                        >
                          <div className="space-y-2">
                            <div className="flex justify-center items-center bg-gray-100 rounded aspect-square">
                              <ArrowLeft
                                aria-hidden="true"
                                className="size-12 text-gray-500"
                                weight="bold"
                              />
                            </div>
                            <div className="space-y-1">
                              <div className="text-xs font-medium text-center">
                                ..
                              </div>
                              <div className="text-xs text-center text-gray-500">
                                Back
                              </div>
                            </div>
                          </div>
                        </button>
                      )}

                      {/* Folders */}
                      {folders.map((folder, folderIndex) => (
                        <button
                          type="button"
                          key={`folder-${folderIndex}`}
                          className="p-2 w-full text-left bg-brand-wool rounded border hover:bg-gray-50"
                          onClick={() =>
                            navigateToFolder(tab.name, folder.name)
                          }
                        >
                          <div className="space-y-2">
                            <div className="flex justify-center items-center bg-gray-100 rounded aspect-square">
                              <FolderOpen
                                aria-hidden="true"
                                className="size-16 text-gray-500"
                                weight="duotone"
                              />
                            </div>
                            <div className="space-y-1">
                              <div className="text-xs font-medium truncate">
                                {folder.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {folder.asset?.length || 0} assets
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}

                      {/* Assets */}
                      {assets.map((asset, assetIndex) => {
                        const thumbnailSrc = cmsImageUrl(
                          asset.permalink || asset.url,
                          asset.last_modified,
                          { width: asset.width, height: asset.height }
                        );

                        return (
                          <div
                            key={`asset-${assetIndex}`}
                            className="p-2 bg-brand-wool rounded border"
                          >
                            <div className="space-y-2">
                              {/* Responsive thumbnail transformed by Cloudflare or Statamic Glide. */}
                              {asset.is_image && thumbnailSrc ? (
                                <div className="overflow-hidden relative bg-gray-100 rounded aspect-square">
                                  <Image
                                    src={thumbnailSrc}
                                    alt={
                                      asset.alt || `Asset ${assetIndex + 1}`
                                    }
                                    fill
                                    sizes="(max-width: 639px) 50vw, (max-width: 1023px) 33vw, 16vw"
                                    className="object-cover"
                                  />
                                </div>
                              ) : (
                                <div className="flex justify-center items-center bg-gray-100 rounded aspect-square">
                                  <div className="text-xs font-bold text-gray-400 uppercase">
                                    {asset.extension}
                                  </div>
                                </div>
                              )}

                              <div className="space-y-1">
                                <div className="text-xs font-medium truncate">
                                  {asset.alt ||
                                    asset.path?.split("/").pop() ||
                                    `Asset ${assetIndex + 1}`}
                                </div>

                                <div className="text-xs text-gray-500">
                                  {asset.extension?.toUpperCase()} •{" "}
                                  {asset.size_mb
                                    ? `${asset.size_mb} MB`
                                    : asset.size}
                                </div>

                                <a
                                  href={`/api/download/${asset.path}`}
                                  target="_blank"
                                  className="inline-flex justify-center items-center px-2 py-1 w-full text-xs font-medium text-white rounded bg-button hover:bg-button/90"
                                  download={true}
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500">
                      No assets available in this folder.
                    </p>
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </Container>
    </ComponentWrapper>
  );
}
